/**
 * Espionage-system tuning constants: spy-report tier odds, counter-espionage
 * scan cooldown/discovery/destroy odds. Sourced from the original game's
 * spy-report and spy-check design (pasted into the implementation plan),
 * adapted to this codebase's actual data model — see
 * `docs/game-details/flight_rules.md`'s Espionage section for the full
 * writeup, including the deliberate deviations (the "none" tier below
 * diff ≤ −3, and the original design's "Defence" info deferred — a Full
 * report is meant to carry the target's defences, and will once a
 * defense/turret building exists).
 *
 * Pure lookup functions that consume these tables live in
 * `packages/game-engine/src/espionage.ts`, mirroring the
 * `DEMOLITION_CHANCE_TABLE` / `calculateDemolitionChance` split in
 * `constants/battle.ts` / `game-engine/src/battle.ts`.
 */
/**
 * Symmetric around an even matchup, and saturating at a 3-level gap in
 * either direction: a 3+ lead for the spy always yields Full, a 3+ lead for
 * the target always yields nothing at all. Every gap in between splits
 * Full/Basic, shifting 10 points per level, so the spy's odds read straight
 * off the level difference. Gaps beyond ±3 add nothing — the table is
 * already at its extreme there.
 */
export const SPY_REPORT_TIER_ODDS = [
    { minDiff: -Infinity, none: 1, full: 0, basic: 0 },
    { minDiff: -2, none: 0, full: 0.20, basic: 0.80 },
    { minDiff: -1, none: 0, full: 0.40, basic: 0.60 },
    { minDiff: 0, none: 0, full: 0.50, basic: 0.50 },
    { minDiff: 1, none: 0, full: 0.60, basic: 0.40 },
    { minDiff: 2, none: 0, full: 0.80, basic: 0.20 },
    { minDiff: 3, none: 0, full: 1, basic: 0 },
];
/**
 * Levels 0-1 have no row of their own in the original table (its lowest
 * band is "2-4") — folded into the same 90-minute band as 2-4 rather than
 * treating counter-espionage as locked out below level 2. Rows above level
 * 20 are included for table completeness (mirrors the original's
 * Endless-universe-only 21-30 brackets) but are unreachable in play since
 * `SCIENCE_DEFINITIONS.intelligence.maxLevel` is 20.
 */
export const SPY_SCAN_COOLDOWN_TABLE = [
    { minLevel: 0, cooldownMinutes: 90 },
    { minLevel: 5, cooldownMinutes: 80 },
    { minLevel: 8, cooldownMinutes: 70 },
    { minLevel: 11, cooldownMinutes: 60 },
    { minLevel: 14, cooldownMinutes: 50 },
    { minLevel: 18, cooldownMinutes: 40 },
    { minLevel: 21, cooldownMinutes: 30 }, // unreachable — max Intelligence level is 20
    { minLevel: 24, cooldownMinutes: 20 }, // unreachable
    { minLevel: 28, cooldownMinutes: 10 }, // unreachable
];
export const SPY_SCAN_DISCOVERY_ODDS = [
    { minDiff: -Infinity, discoverChance: 0.05 },
    { minDiff: -3, discoverChance: 0.25 },
    { minDiff: -1, discoverChance: 0.50 },
    { minDiff: 2, discoverChance: 0.75 },
    { minDiff: 4, discoverChance: 1.0 },
];
/**
 * Flat chance to destroy a probe once discovered — the original table's
 * "no shield" column (30%). The "with shield" column (100%) is not
 * implemented; see `SPY_SCAN_COOLDOWN_TABLE`'s doc comment.
 */
export const SPY_SCAN_DESTROY_CHANCE_NO_SHIELD = 0.30;
//# sourceMappingURL=espionage.js.map